import { DeleteOutlined } from "@ant-design/icons";
import { Button, Statistic } from "antd";
import classNames from "classnames";
import { useKeyFormatter } from "components/Graph/hooks/useKeyFormatter.ts";
import { GraphId, HighlightState } from "components/Graph/types.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useState } from "react";

const DISABLED_COLOR = "rgba(0, 0, 0, 0.25)";

export interface BaseHighlightProps {
    metricKey: string;
    color: string;
    onClick: () => void;
    onHide: () => void;
    state: HighlightState;
    hidden?: boolean;
}

export interface HighlightProps<TData extends object> extends BaseHighlightProps {
    data: TData[];
    graphId: GraphId;
    active?: string[];
}

export const Highlight = <TData extends object = object>({
    metricKey,
    onClick,
    onHide,
    state,
    color,
    data,
    graphId,
    active,
}: HighlightProps<TData>) => {
    const { t } = usePaldockTranslation("ui.report.columns");
    const isDisabled = state === "disabled";

    const [hover, setHover] = useState<boolean>(false);

    const hoverOn = () => setHover(true);
    const hoverOff = () => setHover(false);

    const formattedValue = useKeyFormatter(data, metricKey as keyof TData, graphId);

    const isActive = active?.length === 1 && active[0] === metricKey;

    return (
        <div
            onClick={onClick}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            className={classNames("w-[200px] h-[74px] rounded-lg p-2 relative", {
                "cursor-pointer": !isActive,
                border: !isDisabled,
                "bg-white": !isDisabled,
                "bg-[#f5f5f5]": isDisabled,
            })}
        >
            <div className="flex flex-1 justify-center items-center gap-4">
                <span className="text-md text-gray-500">{t(metricKey)}</span>
                <div
                    className="w-6 h-3 rounded-md"
                    style={{ backgroundColor: isDisabled ? DISABLED_COLOR : color }}
                ></div>
            </div>
            <div className={"flex justify-center mt-1"}>
                <Statistic value={formattedValue} valueStyle={{ color: isDisabled ? DISABLED_COLOR : color }} />
            </div>
            {hover && !isActive && <HideBtn onClick={onHide} />}
        </div>
    );
};

interface HideBtnProps {
    onClick: () => void;
}

export const HideBtn = ({ onClick }: HideBtnProps) => {
    return (
        <Button
            danger
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            type={"primary"}
            size={"small"}
            shape={"circle"}
            icon={<DeleteOutlined />}
            className={"absolute top-[-15px] right-[-5px] cursor-pointer z-10"}
        />
    );
};

export const ExposedHighlight = (props: BaseHighlightProps) => {
    return <Highlight {...(props as unknown as HighlightProps<object>)} />;
};
